import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Science,
  Assignment,
  BarChart,
  PhotoLibrary,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { TEST_CODES } from '../../../constants/testCodes';
import { Breadcrumb } from '../../../components/layout/Breadcrumb';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../../components/shared/ConfirmDialog';
import { TestNotFound } from '../../../components/shared/TestNotFound';
import { ROUTES } from '../../../constants/routes';
import { usePermissions } from '../../../hooks/usePermissions';
import { useUnsavedChangesWarning } from '../../../hooks/useUnsavedChangesWarning';
import { useRegisterUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import { getTriaxialData, getTriaxialCrossTest, saveTriaxialData } from '../../../services/triaxialService';
import { downloadReport } from '../../../services/reportService';
import type {
  TriaxialContext,
  TriaxialCrossTest,
  TriaxialGeneral,
  TriaxialNavigation,
  TriaxialReading,
  TriaxialStage,
} from '../../../types/triaxial';
import { EMPTY_TRIAXIAL_GENERAL } from '../config/triaxialFields';
import { TestFooterActions } from '../components/TestFooterActions';
import { TestActionBar } from '../components/TestActionBar';
import { useReportPreview } from '../../../components/reportPreview/useReportPreview';
import { TriaxialParametersTab } from './TriaxialParametersTab';
import { TriaxialResultEntry } from './TriaxialResultEntry';
import { TriaxialAnalysis } from './TriaxialAnalysis';
import { TriaxialPhotosTab } from '../photos/TriaxialPhotosTab';

const TAB_DEFS = [
  { label: 'Test Parameters',  icon: <Science sx={{ fontSize: 18 }} /> },
  { label: 'Result Entry',     icon: <Assignment sx={{ fontSize: 18 }} /> },
  { label: 'Analysis & Plots', icon: <BarChart sx={{ fontSize: 18 }} /> },
  { label: 'Photos',           icon: <PhotoLibrary sx={{ fontSize: 18 }} /> },
];

const EMPTY_CONTEXT: TriaxialContext = {
  locationName: '', sampleReference: '', specimenRef: null, specimenDepth: null,
  depth: 0, testName: '', workOrderId: '', projectId: '', projectName: '',
  clientName: '', siteName: '', sampleType: '', siteKey: '',
  labLocation: 'Fredericton',
  testedBy: null, testDate: null, reviewedBy: null, reviewDate: null,
  approvedBy: null, approvalDate: null, reportUnit: 'metric', testStatus: '',
};

const EMPTY_NAV: TriaxialNavigation = {
  previousWorkOrderDetailKey: null, nextWorkOrderDetailKey: null,
  currentIndex: 0, totalSamples: 0,
};

export const TriaxialWorkflowPage = () => {
  const { projectId, workOrderId, workOrderDetailKey } = useParams<{
    projectId: string; workOrderId: string; workOrderDetailKey: string;
  }>();
  const navigate = useNavigate();
  const { openPreview, openHistory, previewDialog } = useReportPreview(workOrderDetailKey);
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const { canEditTestData, canSaveTestData } = usePermissions();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeTab, setActiveTabState] = useState(() => {
    const t = parseInt(searchParams.get('tab') || '0', 10);
    return t >= 0 && t < TAB_DEFS.length ? t : 0;
  });
  const setActiveTab = useCallback((tab: number) => {
    setActiveTabState(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 0) next.delete('tab');
      else next.set('tab', String(tab));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [general, setGeneral] = useState<TriaxialGeneral>(EMPTY_TRIAXIAL_GENERAL);
  const [stages, setStages] = useState<TriaxialStage[]>([]);
  const [readings, setReadings] = useState<TriaxialReading[]>([]);
  // Count of 'Excluded' rows (not shipped in `readings` — see TriaxialData).
  const [excludedReadingsCount, setExcludedReadingsCount] = useState(0);
  const [context, setContext] = useState<TriaxialContext>(EMPTY_CONTEXT);
  const [navigation, setNavigation] = useState<TriaxialNavigation>(EMPTY_NAV);
  const [crossTest, setCrossTest] = useState<TriaxialCrossTest | null>(null);
  const [testStatus, setTestStatus] = useState('');

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>(CONFIRM_INITIAL);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [reportProgress, setReportProgress] = useState(0);
  const [reportLabel, setReportLabel] = useState('');

  const loadData = useCallback(async () => {
    if (!workOrderDetailKey) return;
    setLoading(true);
    try {
      const data = await getTriaxialData(workOrderDetailKey);
      const inferredTestType: 'CIU' | 'CID' =
        data.context.testCode === TEST_CODES.triaxialCd ? 'CID' : 'CIU';
      setGeneral(data.general ?? { ...EMPTY_TRIAXIAL_GENERAL, testType: inferredTestType });
      setStages(data.stages);
      setReadings(data.readings);
      setExcludedReadingsCount(data.excludedReadingsCount ?? 0);
      setContext(data.context);
      setNavigation(data.navigation);
      setCrossTest(data.crossTest);
      setTestStatus(data.context.testStatus || '');
      setDirty(false);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setNotFound(true);
      } else {
        setSnackbar({ open: true, message: 'Failed to load test data', severity: 'error' });
      }
    } finally {
      setLoading(false);
    }
  }, [workOrderDetailKey]);

  useEffect(() => { loadData(); }, [loadData]);

  // BUG-2026-0189 — warn on browser refresh / close / back with unsaved edits.
  useUnsavedChangesWarning(dirty);

  // REQ-2026-0002 (REQ-08) — silent cross-test refresh on tab focus.
  //
  // The triaxial test owner commonly hops to Atterberg / PSD in a sibling
  // browser tab, approves them, then comes back; we refresh ONLY the
  // Auto-pulled panel so it stops showing stale "—".
  //
  // This MUST be silent: it updates `crossTest` alone via a light cross-test
  // endpoint. It deliberately does NOT call loadData() — that flipped the
  // loading spinner and re-read the whole (10k-row) test payload, which made
  // the page body visibly reload every time the browser tab regained focus.
  const refreshCrossTest = useCallback(async () => {
    if (!workOrderDetailKey) return;
    try {
      const next = await getTriaxialCrossTest(workOrderDetailKey);
      setCrossTest(next);
    } catch {
      /* best-effort background refresh — never disrupt the page on failure */
    }
  }, [workOrderDetailKey]);

  // Gated to the Parameters tab (activeTab === 0) so we don't churn the
  // network on every tab switch. The `dirty` guard prevents trampling
  // half-typed values: when the operator has unsaved edits, the visible
  // form fields are the source of truth, so we skip the refresh.
  useEffect(() => {
    if (activeTab !== 0) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !dirty) {
        refreshCrossTest();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [activeTab, dirty, refreshCrossTest]);

  const onGeneralChange = useCallback((field: keyof TriaxialGeneral, value: any) => {
    setGeneral((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  }, []);

  const onSilentUpdateGeneral = useCallback((fields: Partial<TriaxialGeneral>) => {
    setGeneral((prev) => ({ ...prev, ...fields }));
  }, []);

  const onNotifyAnalysis = useCallback((message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  }, []);
  const onStagesChange = useCallback((next: TriaxialStage[]) => {
    setStages(next);
    setDirty(true);
  }, []);

  const handleSave = async () => {
    if (!workOrderDetailKey) return;
    setSaving(true);
    try {
      const data = await saveTriaxialData(workOrderDetailKey, general, stages);
      setGeneral(data.general ?? general);
      setStages(data.stages);
      setReadings(data.readings);
      setExcludedReadingsCount(data.excludedReadingsCount ?? 0);
      setCrossTest(data.crossTest);
      setTestStatus(data.context.testStatus || '');
      setDirty(false);
      setSnackbar({ open: true, message: 'Saved', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || 'Save failed', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Auto-save then navigate to the next sample
  const autoSaveIfDirty = async (): Promise<boolean> => {
    if (!dirty || !workOrderDetailKey) return true;
    setSaving(true);
    try {
      await saveTriaxialData(workOrderDetailKey, general, stages);
      setDirty(false);
      return true;
    } catch {
      setSnackbar({ open: true, message: 'Failed to auto-save', severity: 'error' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  // BUG-2026-0189 — register the shared unsaved-changes guard + auto-save on tab switch.
  const requestLeave = useRegisterUnsavedGuard({ dirty, save: () => autoSaveIfDirty(), setConfirm: setConfirmDlg });
  const handleTabChange = (idx: number) => {
    void autoSaveIfDirty();
    setActiveTab(idx);
  };

  const handleNextSample = async () => {
    if (!navigation.nextWorkOrderDetailKey || !projectId || !workOrderId) return;
    if (!(await autoSaveIfDirty())) return;
    navigate(ROUTES.TEST_PARAMETERS
      .replace(':projectId', projectId)
      .replace(':workOrderId', workOrderId)
      .replace(':workOrderDetailKey', navigation.nextWorkOrderDetailKey));
  };

  const handlePreviousSample = async () => {
    if (!navigation.previousWorkOrderDetailKey || !projectId || !workOrderId) return;
    if (!(await autoSaveIfDirty())) return;
    navigate(ROUTES.TEST_PARAMETERS
      .replace(':projectId', projectId)
      .replace(':workOrderId', workOrderId)
      .replace(':workOrderDetailKey', navigation.previousWorkOrderDetailKey));
  };

  const handleCancel = () => {
    const goBack = () => {
      if (projectId && workOrderId) {
        navigate(ROUTES.WORK_ORDER_DETAIL
          .replace(':projectId', projectId)
          .replace(':workOrderId', workOrderId));
      }
    };
    // BUG-2026-0189 — shared Save & Leave / Discard & Leave / Cancel dialog.
    requestLeave(goBack);
  };

  const handleGenerateReport = async () => {
    if (!workOrderDetailKey) return;
    setReportGenerating(true);
    setReportProgress(0);
    setReportLabel('Starting report generation...');
    const steps = [
      { pct: 10, label: 'Collecting test data...', delay: 300 },
      { pct: 30, label: 'Loading readings...', delay: 1000 },
      { pct: 50, label: 'Building report template...', delay: 2500 },
      { pct: 65, label: 'Launching renderer...', delay: 4000 },
      { pct: 80, label: 'Rendering charts...', delay: 6000 },
      { pct: 90, label: 'Generating PDF...', delay: 9000 },
    ];
    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const step = [...steps].reverse().find((s) => elapsed >= s.delay);
      if (step) { setReportProgress(step.pct); setReportLabel(step.label); }
    }, 200);
    try {
      await downloadReport(workOrderDetailKey);
      setReportProgress(100);
      setReportLabel('Download complete');
      setSnackbar({ open: true, message: 'Report downloaded', severity: 'success' });
      setTimeout(() => { setReportProgress(0); setReportLabel(''); }, 2000);
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Report generation failed';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      clearInterval(timer);
      setReportGenerating(false);
    }
  };

  // Breadcrumb path (matches DS style: Client > Project > Site > Location > WO > Sample > Test)
  const breadcrumbs = [
    { label: context.clientName || '...', path: ROUTES.CLIENTS, entityType: 'client' as const },
    { label: context.projectName || context.projectId || '...', path: ROUTES.PROJECTS, entityType: 'project' as const },
    { label: context.siteName || '...',
      path: context.siteKey ? ROUTES.SITE_DETAIL.replace(':siteKey', context.siteKey) : undefined,
      entityType: 'site' as const },
    { label: context.locationName || '...',
      path: projectId ? ROUTES.LOCATIONS.replace(':projectId', projectId) : undefined,
      entityType: 'location' as const },
    { label: context.workOrderId || '...',
      path: projectId && workOrderId && !workOrderId.startsWith(':')
        ? ROUTES.WORK_ORDER_DETAIL.replace(':projectId', projectId).replace(':workOrderId', workOrderId)
        : undefined,
      entityType: 'workOrder' as const },
    { label: context.sampleReference || '...', entityType: 'sample' as const },
    { label: context.testName || '...', entityType: 'test' as const },
  ];

  if (notFound) return <TestNotFound entity="test" />;
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* ── Header row: breadcrumb + depth + status | Generate Report + counter ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Breadcrumb items={breadcrumbs} />
            {context.depth != null && context.depth !== 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Depth: {context.depth} m
              </Typography>
            )}
            {testStatus && (
              <Chip
                label={testStatus}
                size="small"
                color={
                  testStatus === 'Approved' ? 'success'
                  : testStatus === 'Review Completed' ? 'info'
                  : testStatus === 'In Progress' ? 'warning'
                  : 'default'
                }
                variant="filled"
                sx={{ fontSize: '0.7rem', height: 22 }}
              />
            )}
          </Box>
        </Box>
        {previewDialog}
        <TestActionBar
          leadingStatus={reportGenerating && reportLabel ? (
            <Typography variant="body2" className="print-hide" sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'primary.main' }}>
              {reportLabel} ({reportProgress}%)
            </Typography>
          ) : undefined}
          saveHidden={activeTab === 1 || activeTab === 2 /* Result Entry / Analysis save through their own flows; BUG-2026-0153 keeps Save on Photos */}
          canSave={canSaveTestData}
          onSave={handleSave}
          saving={saving}
          dirty={dirty}
          onPrint={() => {
            if (activeTab !== 0) setActiveTab(0);
            setTimeout(() => window.print(), 60);
          }}
          canPrint={!reportGenerating}
          onGenerateReport={handleGenerateReport}
          onPreviewReport={openPreview}
          onReportHistory={openHistory}
          reportGenerating={reportGenerating}
          pagerLabel={`${navigation.currentIndex} / ${navigation.totalSamples}`}
        />
      </Box>
      {reportGenerating && (
        <LinearProgress variant="determinate" value={reportProgress} className="print-hide" sx={{ height: 4, mb: 0.5, borderRadius: 2 }} />
      )}

      {/* ── Tab bar — full-width 4-column grid (matches DS) ── */}
      <Box className="print-hide" sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', mb: 1.5 }}>
        {TAB_DEFS.map((tab, idx) => {
          const isActive = activeTab === idx;
          return (
            <Box
              key={tab.label}
              onClick={() => handleTabChange(idx)}
              sx={{
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                borderBottom: isActive ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                backgroundColor: isActive ? theme.palette.background.paper : '#F0F0EA',
                color: isActive ? theme.palette.text.primary : theme.palette.text.secondary,
                fontWeight: isActive ? 700 : 400,
                '&:hover': {
                  backgroundColor: isActive
                    ? theme.palette.background.paper
                    : alpha(theme.palette.primary.main, 0.06),
                },
                '& .MuiSvgIcon-root': {
                  color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                  fontSize: 18,
                },
              }}
            >
              {tab.icon}
              <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 'inherit' }}>
                {tab.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* ── Body ── */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {activeTab === 0 && (
          <TriaxialParametersTab
            general={general}
            stages={stages}
            crossTest={crossTest}
            onChange={onGeneralChange}
            onStagesChange={onStagesChange}
            readOnly={!canEditTestData}
            workOrderDetailKey={workOrderDetailKey}
          />
        )}
        {activeTab === 1 && workOrderDetailKey && (
          <TriaxialResultEntry
            workOrderDetailKey={workOrderDetailKey}
            general={general}
            readings={readings}
            excludedReadingsCount={excludedReadingsCount}
            crossTest={crossTest}
            onReloadRequested={loadData}
            hasUnsavedParameters={dirty}
            readOnly={!canEditTestData}
          />
        )}
        {activeTab === 2 && workOrderDetailKey && (
          <TriaxialAnalysis
            workOrderDetailKey={workOrderDetailKey}
            general={general}
            readings={readings}
            onReloadRequested={loadData}
            onSilentUpdateGeneral={onSilentUpdateGeneral}
            onNotify={onNotifyAnalysis}
            readOnly={!canEditTestData}
          />
        )}
        {activeTab === 3 && workOrderDetailKey && (
          <TriaxialPhotosTab workOrderDetailKey={workOrderDetailKey} />
        )}
      </Box>

      {/* ── Sticky bottom action bar (Parameters tab only, matching DS) ── */}
      {activeTab === 0 && (
        <Box className="print-hide">
          <TestFooterActions
            onSave={handleSave}
            onNextSample={handleNextSample}
            onPreviousSample={handlePreviousSample}
            onCancel={handleCancel}
            saving={saving}
            dirty={dirty}
            hasNext={!!navigation.nextWorkOrderDetailKey}
            hasPrevious={!!navigation.previousWorkOrderDetailKey}
            hideSave={!canSaveTestData}
            pagerLabel={`${navigation.currentIndex} / ${navigation.totalSamples}`}
          />
        </Box>
      )}

      <ConfirmDialog state={confirmDlg} onClose={() => setConfirmDlg(CONFIRM_INITIAL)} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'success' ? 2000 : 4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
